import React, { useState, useEffect } from "react";
import { Box, Typography, Breadcrumbs, Link, useTheme } from "@mui/material";
import {
  LocalShipping as ShippingIcon,
  Home as HomeIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import { StockTransferList } from "../../components/StockTransferList/StockTransferList";
import { StockTransferForm } from "../../components/StockTransferForm/StockTransferForm";
import { StockTransfer } from "../../types/stock-transfer.types";

type ViewMode = "list" | "create" | "edit" | "view";

export const StockTransferPage: React.FC = () => {
  const theme = useTheme();
  const [currentView, setCurrentView] = useState<ViewMode>("list");
  const [selectedTransfer, setSelectedTransfer] = useState<
    StockTransfer | undefined
  >();

  const handleCreateTransfer = () => {
    setSelectedTransfer(undefined);
    setCurrentView("create");
  };

  const handleEditTransfer = (transfer: StockTransfer) => {
    setSelectedTransfer(transfer);
    setCurrentView("edit");
  };

  const handleViewTransfer = (transfer: StockTransfer) => {
    setSelectedTransfer(transfer);
    setCurrentView("view");
  };

  const handleFormSave = (transfer: StockTransfer) => {
    setCurrentView("list");
    setSelectedTransfer(undefined);
  };

  const handleFormCancel = () => {
    setCurrentView("list");
    setSelectedTransfer(undefined);
  };

  const getBreadcrumbs = () => {
    const breadcrumbs = [
      {
        label: "Dashboard",
        href: "/business",
        icon: <HomeIcon fontSize="inherit" />,
      },
      {
        label: "Inventory",
        href: "/business/stock-management/raw-materials",
        icon: <InventoryIcon fontSize="inherit" />,
      },
      {
        label: "Stock Transfers",
        href: "/business/stock-management/stock-transfers",
        icon: <ShippingIcon fontSize="inherit" />,
        active: currentView === "list",
      },
    ];

    if (currentView === "create") {
      breadcrumbs.push({
        label: "Create Transfer",
        href: "#",
        icon: <ShippingIcon fontSize="inherit" />,
        active: true,
      });
    } else if (currentView === "edit") {
      breadcrumbs.push({
        label: "Edit Transfer",
        href: "#",
        icon: <ShippingIcon fontSize="inherit" />,
        active: true,
      });
    } else if (currentView === "view") {
      breadcrumbs.push({
        label: "View Transfer",
        href: "#",
        icon: <ShippingIcon fontSize="inherit" />,
        active: true,
      });
    }

    return breadcrumbs;
  };

  const getPageTitle = () => {
    switch (currentView) {
      case "create":
        return "Create Stock Transfer";
      case "edit":
        return "Edit Stock Transfer";
      case "view":
        return "View Stock Transfer";
      default:
        return "Stock Transfers";
    }
  };

  const getPageDescription = () => {
    switch (currentView) {
      case "create":
        return "Create a new stock transfer between locations";
      case "edit":
        return "Edit existing stock transfer details";
      case "view":
        return "View stock transfer details and history";
      default:
        return "Manage stock transfers between warehouses and branches";
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case "create":
        return (
          <StockTransferForm
            mode="create"
            onSave={handleFormSave}
            onCancel={handleFormCancel}
          />
        );
      case "edit":
        return (
          <StockTransferForm
            mode="edit"
            transfer={selectedTransfer}
            onSave={handleFormSave}
            onCancel={handleFormCancel}
          />
        );
      case "view":
        return (
          <StockTransferForm
            mode="edit"
            transfer={selectedTransfer}
            onSave={handleFormSave}
            onCancel={handleFormCancel}
          />
        );
      default:
        return (
          <StockTransferList
            onCreateTransfer={handleCreateTransfer}
            onEditTransfer={handleEditTransfer}
            onViewTransfer={handleViewTransfer}
          />
        );
    }
  };

  return (
    <Box
      sx={{ width: "100%", minHeight: "100vh", bgcolor: "background.default" }}
    >
      {/* Page Header */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          py: 2,
          px: 3,
          mb: 3,
        }}
      >
        {/* Breadcrumbs */}
        <Breadcrumbs separator="›" sx={{ mb: 2 }} aria-label="breadcrumb">
          {getBreadcrumbs().map((breadcrumb, index) => (
            <Link
              key={index}
              underline="hover"
              color={breadcrumb.active ? "text.primary" : "inherit"}
              href={breadcrumb.href}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.875rem",
                fontWeight: breadcrumb.active ? 600 : 400,
              }}
            >
              {breadcrumb.icon}
              {breadcrumb.label}
            </Link>
          ))}
        </Breadcrumbs>

        {/* Page Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ShippingIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
              {getPageTitle()}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {getPageDescription()}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ px: 3, pb: 3 }}>{renderCurrentView()}</Box>
    </Box>
  );
};

export default StockTransferPage;
